import { ColScaffold, RowScaffold, ScaffoldSelection } from "../../interface";
import { addSelectedCell } from "./addSelectedCell";
import { isCellInSelection } from "./isCellInSelection";
import { isColInSelection } from "./isColInSelection";
import { isRowInSelection } from "./isRowInSelection";
import { toSelectedCells } from "./toSelectedCells";

/**
 * Toggles a cell in the selectedCells list, adding it if absent or removing it if present.
 * Preserves all row and column selections.
 *
 * @param selection - The current scaffold selection state.
 * @param prevRows - Current scaffold rows used to expand row-derived cell selections.
 * @param prevCols - Current scaffold columns used to expand column-derived cell selections.
 * @param rowId - The row ID of the cell to toggle.
 * @param colId - The column ID of the cell to toggle.
 * @returns A new selection state with the cell toggled.
 */
export const toggleCellInSelection = <T>(
  selection: ScaffoldSelection,
  prevRows: RowScaffold<T>[],
  prevCols: ColScaffold<T>[],
  rowId: string,
  colId: string,
): ScaffoldSelection => {
  if (isCellInSelection(selection, rowId, colId)) {
    const newSelectedCells = selection.selectedCells
      .map((r) => (r.rowId === rowId ? { ...r, colId: r.colId.filter((c) => c !== colId) } : r))
      .filter((r) => r.colId.length > 0);
    return { ...selection, selectedCells: newSelectedCells };
  }

  const rowSelected = isRowInSelection(selection, rowId);
  const colSelected = isColInSelection(selection, colId);

  if (rowSelected || colSelected) {
    const selectedCellMap = new Map<string, Set<string>>();
    selection.selectedCells.forEach((selectedRow) => {
      selectedRow.colId.forEach((selectedColId) => {
        addSelectedCell(selectedCellMap, selectedRow.rowId, selectedColId);
      });
    });

    if (rowSelected) {
      prevCols.forEach((col) => {
        if (col.id !== colId) {
          addSelectedCell(selectedCellMap, rowId, col.id);
        }
      });
    }

    if (colSelected) {
      prevRows.forEach((row) => {
        if (row.id !== rowId) {
          addSelectedCell(selectedCellMap, row.id, colId);
        }
      });
    }

    selectedCellMap.get(rowId)?.delete(colId);

    return {
      ...selection,
      selectedCells: toSelectedCells(selectedCellMap, prevRows, prevCols),
      selectedRows: rowSelected
        ? selection.selectedRows.filter((selectedRow) => selectedRow.firstCellId !== rowId)
        : selection.selectedRows,
      selectedCols: colSelected
        ? selection.selectedCols.filter((selectedCol) => selectedCol.firstCellId !== colId)
        : selection.selectedCols,
    };
  }

  const existingRow = selection.selectedCells.find((r) => r.rowId === rowId);
  if (existingRow) {
    return {
      ...selection,
      selectedCells: selection.selectedCells.map((r) =>
        r.rowId === rowId ? { ...r, colId: [...r.colId, colId] } : r,
      ),
    };
  }

  return {
    ...selection,
    selectedCells: [...selection.selectedCells, { rowId, colId: [colId] }],
  };
};
